import Head from 'next/head';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';

const leafletPath = '/vendor/leaflet-1.9.4';

export default function ShopMap({ shop, t }) {
  const container = useRef(null);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const { lat, lon } = shop.coordinates;
  const name = `KRIT ${shop.city}`;
  const zoomIn = t('visit.zoomIn');
  const zoomOut = t('visit.zoomOut');

  useEffect(() => {
    if (!window.IntersectionObserver) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!ready || !window.L) return;
    const L = window.L;
    const map = L.map(container.current, { scrollWheelZoom: false, zoomControl: false,
      dragging: window.matchMedia('(pointer: fine)').matches, touchZoom: false })
      .setView([lat, lon], 16);
    L.control.zoom({ zoomInTitle: zoomIn, zoomOutTitle: zoomOut }).addTo(map);
    const tiles = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    tiles.on('load', () => {
      const loaded = Array.from(container.current?.querySelectorAll('.leaflet-tile') || [])
        .some(tile => tile.complete && tile.naturalWidth > 0);
      setUnavailable(!loaded);
    });
    const icon = L.icon({
      iconUrl: '/icons/logo-vector.svg',
      iconSize: [48, 48], iconAnchor: [24, 48], popupAnchor: [0, -44],
      className: 'rounded-full border-2 border-[var(--shop-accent)] bg-white object-contain p-1 shadow-lg',
    });
    const popup = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = name;
    popup.append(title, document.createElement('br'), shop.street);
    L.marker([lat, lon], { icon, title: name, alt: name }).addTo(map).bindPopup(popup);
    const resize = new ResizeObserver(() => map.invalidateSize({ pan: false }));
    resize.observe(container.current);
    return () => {
      resize.disconnect();
      map.remove();
    };
  }, [ready, lat, lon, name, shop.street, zoomIn, zoomOut]);

  return <div role="region" aria-label={`${t('visit.mapTitle')} ${shop.city}`} className="relative isolate min-h-[340px] w-full flex-1 sm:min-h-[400px]">
    <Head><link rel="stylesheet" href={`${leafletPath}/leaflet.css`} /></Head>
    {visible && <Script src={`${leafletPath}/leaflet.js`} strategy="afterInteractive" onReady={() => setReady(true)} onError={() => setUnavailable(true)} />}
    <div ref={container} className="absolute inset-0 z-0" />
    {(!ready || unavailable) && <p role="status" className="pointer-events-none absolute inset-x-4 top-4 z-10 rounded-lg bg-white/95 p-3 text-center text-xs text-[var(--shop-ink)] shadow-sm">{t(unavailable ? 'visit.mapUnavailable' : 'visit.mapLoading')}</p>}
  </div>;
}
